import Button from '@material-ui/core/Button'
import Grid from '@material-ui/core/Grid'
import { styled } from '@material-ui/styles'
import { FC, useRef } from 'react'
import { Form as FinalForm, FormProps } from 'react-final-form'
import pendingStatus from '../../../../constants/pendingStatus'
import projectStatus from '../../../../constants/projectStatus'
import { ButtonView } from '../../../../control/button/button'
import Dialog from '../../../../control/dialog/dialog'
import useLoading from '../../../../control/loading/useLoading'
import MyTextfield from '../../../../control/textfield/textfield'
import useTranslate from '../../../../i18n/useTranslate'
import { ItemListType } from '../../../../services/web/types/responseWebServiceTypes'
import useWebService from '../../../../services/web/useWebService'
import useAuthenStore from '../../../../stores/authen/authenStore'
import useCheckPermission from '../../../../utilities/checkPermission'
import AccessTimeIcon from '@material-ui/icons/AccessTime'

const PendingButton = styled(ButtonView)({
    backgroundColor: '#379956',
    color: '#fff',
    marginRight: '1px',
    '&:hover': {
        backgroundColor: '#37997C',
    },
})

const ResumeButton = styled(ButtonView)({
    backgroundColor: '#A91D1D',
    color: '#fff',
    marginRight: '1px',
    '&:hover': {
        backgroundColor: '#A91D38',
    },
})

const CloseButton = styled(Button)({
    border: '1px solid #DB5F8E',
    boxShadow: '0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%)',
})

const DialogPending: FC<{ item: ItemListType; pendingProps: any }> = ({ item, pendingProps }) => {
    const isProcessing = item.projectStatusID === projectStatus.processing
    const isResume = item.pendingStatusID === pendingStatus.pending
    const { addPendingAndResume } = useWebService()
    const authenStore = useAuthenStore()
    const translate = useTranslate()
    const dialogRef = useRef<Record<string, Function>>()
    const successRef = useRef<Record<string, Function>>()
    const failRef = useRef<Record<string, Function>>()
    const confirmRef = useRef<Record<string, Function>>()
    const loadingHook = useLoading()
    const checkPermissionHook = useCheckPermission()

    const bindingDialogRef = (func: Record<string, Function>) => {
        dialogRef.current = { ...func }
    }

    const handleCloseDialog = () => {
        dialogRef?.current?.closeDialog()
    }

    const handleOpenDialog = async () => {
        if (dialogRef?.current) {
            dialogRef?.current?.openDialog()
        }
    }

    const callPendingResume = async (formVal?: any) => {
        confirmRef.current?.closeDialog()
        loadingHook.loadingOpen()
        const conditionStatusID = isResume ? pendingStatus.resume : pendingStatus.pending
        await addPendingAndResume({
            submittedID: item.submittedID,
            statusID: conditionStatusID,
            /**
             * StatusID 1 is Pending
             * StatusID 2 is Resume
             */
            remarks: formVal?.remark ?? null,
            userID: authenStore.profile.userId,
        })
            .then(() => {
                handleCloseDialog()
                loadingHook.loadingClose()
                successRef.current?.openDialog()
            })
            .catch(() => {
                loadingHook.loadingClose()
                failRef.current?.openDialog()
            })
    }

    const validateForm = (values: FormProps) => {
        const errors: any = {}
        if (!values.remark || values.remark === '') {
            errors.passcode = translate.get('MESSAGE.M023')
        }

        return errors
    }

    const onSetSuccessRef = (func: Record<string, Function>) => {
        successRef.current = { ...func }
    }

    const onSetFailRef = (func: Record<string, Function>) => {
        failRef.current = { ...func }
    }

    const onSetConfirmRef = (func: Record<string, Function>) => {
        confirmRef.current = { ...func }
    }

    const afterSubmit = () => {
        const param = pendingProps.searchParamRef.current
        if (param) {
            pendingProps.afterSubmitPending(param)
        }
    }

    const openConfirm = () => {
        confirmRef.current?.openDialog()
    }

    const renderButton = () => {
        return item.pendingStatusID === pendingStatus.pending ? (
            <ResumeButton
                style={{ visibility: isProcessing && checkPermissionHook.isUserAdmin() ? 'visible' : 'hidden' }}
                startIcon={<AccessTimeIcon />}
                onClick={openConfirm}
            >
                {translate.get('BUTTON.RESUME')}
            </ResumeButton>
        ) : (
            <PendingButton
                style={{ visibility: isProcessing && checkPermissionHook.isUserAdmin() ? 'visible' : 'hidden' }}
                startIcon={<AccessTimeIcon />}
                onClick={handleOpenDialog}
            >
                {translate.get('BUTTON.PENDING')}
            </PendingButton>
        )
    }

    return (
        <>
            {renderButton()}
            <Dialog
                maxWidth="xs"
                onRef={bindingDialogRef}
                type=""
                title={translate.get('LABEL.PENDING_REASON')}
                content={
                    <FinalForm onSubmit={callPendingResume} initialValues={{ remark: '' }} validate={validateForm}>
                        {({ handleSubmit }) => {
                            return (
                                <>
                                    <MyTextfield
                                        name="remark"
                                        materialInputProps={{
                                            label: translate.get('LABEL.PLEASE_SPECIFY_REMARK'),
                                            disabled: false,
                                            placeholder: translate.get('LABEL.PLEASE_SPECIFY_REMARK'),
                                            size: 'medium',
                                            rows: 5,
                                            multiline: true,
                                        }}
                                        required
                                    />
                                    <pre />
                                    <Grid container justify="center" alignItems="center" spacing={1}>
                                        <Grid item>
                                            <CloseButton onClick={handleCloseDialog}>
                                                {translate.get('BUTTON.CANCEL')}
                                            </CloseButton>
                                        </Grid>
                                        <Grid item>
                                            <ButtonView color="primary" type="submit" onClick={handleSubmit}>
                                                {translate.get('BUTTON.SAVE')}
                                            </ButtonView>
                                        </Grid>
                                    </Grid>
                                </>
                            )
                        }}
                    </FinalForm>
                }
            />
            <Dialog
                onRef={onSetSuccessRef}
                type="alert-message"
                title={translate.get('ALERT.TITLE.NOTIFICATION')}
                content={translate.get('ALERT.CONTENT.SAVE_SUCCESS')}
                onAfterClose={afterSubmit}
            />
            <Dialog
                onRef={onSetFailRef}
                type="alert-message"
                title={translate.get('ALERT.TITLE.NOTIFICATION')}
                content={translate.get('ALERT.CONTENT.SAVE_NOT_SUCCESS')}
            />
            <Dialog
                onRef={onSetConfirmRef}
                type="alert-confirm"
                title={translate.get('ALERT.TITLE.NOTIFICATION')}
                content={translate.get('ALERT.CONTENT.CONFIRM_RESUME')}
                onClickConfirm={callPendingResume}
            />
        </>
    )
}

export default DialogPending
